import {
  ComputationKind,
  Direction,
  Insight,
  InsightKind,
  TriggerType,
} from '@fullstory/proto/fs/api/proactiveinsights';
import { GetInsightsResponse } from '@fullstory/proto/fs/services/pie';
import { Timestamp, int64 } from '@fullstory/proto/google/protobuf';
import { now } from 'moment';
import avgMonthlyInsight from './responseJson/avgMonthlyInsight.json';
import nanVolBug from './responseJson/nanVolBug.json';
import nonZeroVolMin from './responseJson/nonZeroVolMin.json';
import { insightJsonToProto } from './responseJson/utils';
import volMonthlyInsight from './responseJson/volMonthlyInsight.json';
import { GetInsightDetailViewResponse } from '@fullstory/proto/fs/services/dx-objects';
import { newTestDeps } from '../appbackend';
import { mockTrendMetric } from '../savedMetrics';

export const MOCK_VOL_NAN_INSIGHT: Insight = insightJsonToProto(nanVolBug);
export const MOCK_VOL_NONZERO_Y_MIN: Insight = insightJsonToProto(nonZeroVolMin);
export const MOCK_AVG_MONTHLY_INSIGHT: Insight = insightJsonToProto(avgMonthlyInsight);
export const MOCK_VOL_MONTHLY_INSIGHT: Insight = insightJsonToProto(volMonthlyInsight);

// Uses with the mockInitialSavedMetricsState metric ID, and version.
export const GET_ALL_RESPONSE: GetInsightsResponse = {
  insights: [
    {
      insightId: '1',
      kind: InsightKind.INSIGHT_KIND_WEEK_OVER_WEEK,
      computationKind: ComputationKind.COMPUTATION_KIND_TREND,
      computationHash: 'testComputationHash',
      dxObject: {
        id: 'test',
        version: int64.fromNumber(0),
        supportedDxObjectType: 1,
      },
      computedAt: Timestamp.fromMillis(now()),
      start: Timestamp.fromMillis(1649861973000),
      end: Timestamp.fromMillis(now()),
      createdAt: Timestamp.fromMillis(now()),
      updatedAt: null,
      alertMetadata: null,
      trend: {
        data: null,
        direction: Direction.DIRECTION_INCREASING,
        ratioChange: 0.12,
        periodicStats: [
          {
            startDate: Timestamp.fromDate(new Date('2022-06-16T04:00:00Z')),
            endDate: Timestamp.fromDate(new Date('2022-06-23T04:00:00Z')),
            mean: 1.1428571428571428,
          },
          {
            startDate: Timestamp.fromDate(new Date('2022-06-23T04:00:00Z')),
            endDate: Timestamp.fromDate(new Date('2022-06-30T04:00:00Z')),
            mean: 1.5714285714285714,
          },
          {
            startDate: Timestamp.fromDate(new Date('2022-06-30T04:00:00Z')),
            endDate: Timestamp.fromDate(new Date('2022-07-07T04:00:00Z')),
            mean: 1.1428571428571428,
          },
          {
            startDate: Timestamp.fromDate(new Date('2022-07-07T04:00:00Z')),
            endDate: Timestamp.fromDate(new Date('2022-07-14T04:00:00Z')),
            mean: 4.428571428571429,
          },
        ],
        trendSteps: null,
      },
    },
    {
      insightId: '2',
      kind: InsightKind.INSIGHT_KIND_WEEK_OVER_WEEK,
      computationKind: ComputationKind.COMPUTATION_KIND_TREND,
      computationHash: 'testComputationHash',
      dxObject: {
        id: 'test',
        version: int64.fromNumber(0),
        supportedDxObjectType: 1,
      },
      computedAt: Timestamp.fromMillis(1649861973000),
      start: Timestamp.fromMillis(1644764774000),
      end: Timestamp.fromMillis(1649861973000),
      createdAt: Timestamp.fromMillis(1649861973000),
      updatedAt: null,
      alertMetadata: null,
      trend: {
        data: null,
        direction: Direction.DIRECTION_DECREASING,
        ratioChange: 1.45,
        periodicStats: [],
        trendSteps: null,
      },
    },
  ],
};

export const GET_INSIGHT_DETAIL_RESPONSE: GetInsightDetailViewResponse = {
  insights: GET_ALL_RESPONSE.insights,
  deps: newTestDeps({
    metrics: {
      test: mockTrendMetric({ name: 'test' }),
    },
  }),
};

export const MOCK_AVG_WEEKLY_INSIGHT: Insight = {
  insightId: '1VKJnzSGOvN8',
  kind: 1,
  computationKind: 3,
  computationHash: 'testComputationHash',
  alertMetadata: null,
  dxObject: {
    id: 'UQeHl5svBgzg',
    version: int64.fromNumber(1657646950638),
    supportedDxObjectType: 1,
  },
  computedAt: Timestamp.fromDate(new Date('2022-07-13T13:56:51.355287061Z')),
  start: Timestamp.fromDate(new Date('2022-06-16T04:00:00Z')),
  end: Timestamp.fromDate(new Date('2022-07-14T04:00:00Z')),
  createdAt: Timestamp.fromDate(new Date('2022-07-13T13:56:51.377271Z')),
  updatedAt: Timestamp.fromDate(new Date('2022-07-13T13:56:51.377271Z')),
  trend: {
    data: {
      metricsDataTable: {
        table: [
          {
            startTime: Timestamp.fromDate(new Date('2022-06-13T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-14T04:00:00Z')),
            groups: [],
            value: 3,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-14T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-15T04:00:00Z')),
            groups: [],
            value: 4,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-15T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-16T04:00:00Z')),
            groups: [],
            value: 1,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-16T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-17T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-17T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-18T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-18T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-19T04:00:00Z')),
            groups: [],
            value: 2,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-19T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-20T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-20T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-21T04:00:00Z')),
            groups: [],
            value: 5,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-21T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-22T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-22T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-23T04:00:00Z')),
            groups: [],
            value: 1,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-23T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-24T04:00:00Z')),
            groups: [],
            value: 3,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-24T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-25T04:00:00Z')),
            groups: [],
            value: 1,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-25T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-26T04:00:00Z')),
            groups: [],
            value: 1,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-26T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-27T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-27T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-28T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-28T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-29T04:00:00Z')),
            groups: [],
            value: 5,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-29T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-30T04:00:00Z')),
            groups: [],
            value: 1,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-30T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-01T04:00:00Z')),
            groups: [],
            value: 3,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-01T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-02T04:00:00Z')),
            groups: [],
            value: 2,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-02T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-03T04:00:00Z')),
            groups: [],
            value: 2,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-03T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-04T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-04T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-05T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-05T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-06T04:00:00Z')),
            groups: [],
            value: 1,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-06T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-07T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-07T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-08T04:00:00Z')),
            groups: [],
            value: 2,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-08T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-09T04:00:00Z')),
            groups: [],
            value: 7,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-09T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-10T04:00:00Z')),
            groups: [],
            value: 1,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-10T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-11T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-11T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-12T04:00:00Z')),
            groups: [],
            value: 9,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-12T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-13T04:00:00Z')),
            groups: [],
            value: 11,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-13T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-14T04:00:00Z')),
            groups: [],
            value: 1,
            defaultValue: 0,
            isPartialPeriod: false,
          },
        ],
        numGroups: int64.fromNumber(0),
      },
    },
    direction: 1,
    ratioChange: 2.483146067415731,
    periodicStats: [
      {
        startDate: Timestamp.fromDate(new Date('2022-06-16T04:00:00Z')),
        endDate: Timestamp.fromDate(new Date('2022-06-23T04:00:00Z')),
        mean: 1.1428571428571428,
      },
      {
        startDate: Timestamp.fromDate(new Date('2022-06-23T04:00:00Z')),
        endDate: Timestamp.fromDate(new Date('2022-06-30T04:00:00Z')),
        mean: 1.5714285714285714,
      },
      {
        startDate: Timestamp.fromDate(new Date('2022-06-30T04:00:00Z')),
        endDate: Timestamp.fromDate(new Date('2022-07-07T04:00:00Z')),
        mean: 1.1428571428571428,
      },
      {
        startDate: Timestamp.fromDate(new Date('2022-07-07T04:00:00Z')),
        endDate: Timestamp.fromDate(new Date('2022-07-14T04:00:00Z')),
        mean: 4.428571428571429,
      },
    ],
    trendSteps: null,
  },
};

export const MOCK_VOL_WEEKLY_INSIGHT: Insight = {
  insightId: 'c1a5ta5GcNjq',
  kind: 1,
  computationKind: 3,
  computationHash: 'testComputationHash',
  dxObject: {
    id: 'dUepQnDR4cA1',
    version: int64.fromNumber(1631908392118),
    supportedDxObjectType: 1,
  },
  alertMetadata: null,
  computedAt: Timestamp.fromDate(new Date('2022-08-18T18:31:02.641319193Z')),
  start: Timestamp.fromDate(new Date('2022-07-21T04:00:00Z')),
  end: Timestamp.fromDate(new Date('2022-08-18T04:00:00Z')),
  createdAt: Timestamp.fromDate(new Date('2022-08-18T18:31:02.749714Z')),
  updatedAt: Timestamp.fromDate(new Date('2022-08-18T18:31:02.749714Z')),
  trend: {
    data: {
      metricsDataTable: {
        table: [
          {
            startTime: Timestamp.fromDate(new Date('2022-03-31T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-01T04:00:00Z')),
            groups: [],
            value: 18,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-01T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-02T04:00:00Z')),
            groups: [],
            value: 17,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-02T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-03T04:00:00Z')),
            groups: [],
            value: 7,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-03T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-04T04:00:00Z')),
            groups: [],
            value: 3,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-04T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-05T04:00:00Z')),
            groups: [],
            value: 18,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-05T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-06T04:00:00Z')),
            groups: [],
            value: 32,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-06T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-07T04:00:00Z')),
            groups: [],
            value: 23,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-07T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-08T04:00:00Z')),
            groups: [],
            value: 22,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-08T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-09T04:00:00Z')),
            groups: [],
            value: 17,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-09T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-10T04:00:00Z')),
            groups: [],
            value: 6,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-10T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-11T04:00:00Z')),
            groups: [],
            value: 1,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-11T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-12T04:00:00Z')),
            groups: [],
            value: 17,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-12T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-13T04:00:00Z')),
            groups: [],
            value: 14,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-13T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-14T04:00:00Z')),
            groups: [],
            value: 10,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-14T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-15T04:00:00Z')),
            groups: [],
            value: 19,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-15T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-16T04:00:00Z')),
            groups: [],
            value: 13,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-16T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-17T04:00:00Z')),
            groups: [],
            value: 25,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-17T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-18T04:00:00Z')),
            groups: [],
            value: 6,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-18T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-19T04:00:00Z')),
            groups: [],
            value: 10,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-19T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-20T04:00:00Z')),
            groups: [],
            value: 14,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-20T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-21T04:00:00Z')),
            groups: [],
            value: 18,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-21T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-22T04:00:00Z')),
            groups: [],
            value: 10,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-22T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-23T04:00:00Z')),
            groups: [],
            value: 17,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-23T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-24T04:00:00Z')),
            groups: [],
            value: 9,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-24T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-25T04:00:00Z')),
            groups: [],
            value: 12,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-25T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-26T04:00:00Z')),
            groups: [],
            value: 10,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-26T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-27T04:00:00Z')),
            groups: [],
            value: 24,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-27T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-28T04:00:00Z')),
            groups: [],
            value: 12,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-28T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-29T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-29T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-04-30T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-04-30T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-01T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-01T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-02T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-02T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-03T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-03T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-04T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-04T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-05T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-05T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-06T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-06T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-07T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-07T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-08T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-08T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-09T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-09T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-10T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-10T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-11T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-11T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-12T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-12T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-13T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-13T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-14T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-14T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-15T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-15T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-16T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-16T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-17T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-17T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-18T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-18T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-19T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-19T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-20T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-20T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-21T04:00:00Z')),
            groups: [],
            value: 14,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-21T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-22T04:00:00Z')),
            groups: [],
            value: 13,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-22T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-23T04:00:00Z')),
            groups: [],
            value: 9,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-23T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-24T04:00:00Z')),
            groups: [],
            value: 15,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-24T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-25T04:00:00Z')),
            groups: [],
            value: 32,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-25T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-26T04:00:00Z')),
            groups: [],
            value: 43,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-26T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-27T04:00:00Z')),
            groups: [],
            value: 37,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-27T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-28T04:00:00Z')),
            groups: [],
            value: 13,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-28T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-29T04:00:00Z')),
            groups: [],
            value: 6,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-29T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-30T04:00:00Z')),
            groups: [],
            value: 6,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-30T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-05-31T04:00:00Z')),
            groups: [],
            value: 21,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-05-31T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-01T04:00:00Z')),
            groups: [],
            value: 21,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-01T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-02T04:00:00Z')),
            groups: [],
            value: 20,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-02T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-03T04:00:00Z')),
            groups: [],
            value: 31,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-03T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-04T04:00:00Z')),
            groups: [],
            value: 42,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-04T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-05T04:00:00Z')),
            groups: [],
            value: 3,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-05T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-06T04:00:00Z')),
            groups: [],
            value: 3,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-06T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-07T04:00:00Z')),
            groups: [],
            value: 10,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-07T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-08T04:00:00Z')),
            groups: [],
            value: 13,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-08T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-09T04:00:00Z')),
            groups: [],
            value: 9,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-09T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-10T04:00:00Z')),
            groups: [],
            value: 55,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-10T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-11T04:00:00Z')),
            groups: [],
            value: 27,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-11T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-12T04:00:00Z')),
            groups: [],
            value: 24,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-12T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-13T04:00:00Z')),
            groups: [],
            value: 10,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-13T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-14T04:00:00Z')),
            groups: [],
            value: 12,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-14T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-15T04:00:00Z')),
            groups: [],
            value: 25,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-15T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-16T04:00:00Z')),
            groups: [],
            value: 16,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-16T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-17T04:00:00Z')),
            groups: [],
            value: 19,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-17T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-18T04:00:00Z')),
            groups: [],
            value: 2,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-18T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-19T04:00:00Z')),
            groups: [],
            value: 1,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-19T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-20T04:00:00Z')),
            groups: [],
            value: 3,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-20T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-21T04:00:00Z')),
            groups: [],
            value: 6,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-21T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-22T04:00:00Z')),
            groups: [],
            value: 9,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-22T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-23T04:00:00Z')),
            groups: [],
            value: 27,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-23T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-24T04:00:00Z')),
            groups: [],
            value: 28,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-24T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-25T04:00:00Z')),
            groups: [],
            value: 11,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-25T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-26T04:00:00Z')),
            groups: [],
            value: 0,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-26T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-27T04:00:00Z')),
            groups: [],
            value: 3,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-27T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-28T04:00:00Z')),
            groups: [],
            value: 24,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-28T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-29T04:00:00Z')),
            groups: [],
            value: 13,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-29T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-06-30T04:00:00Z')),
            groups: [],
            value: 12,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-06-30T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-01T04:00:00Z')),
            groups: [],
            value: 23,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-01T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-02T04:00:00Z')),
            groups: [],
            value: 36,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-02T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-03T04:00:00Z')),
            groups: [],
            value: 1,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-03T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-04T04:00:00Z')),
            groups: [],
            value: 4,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-04T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-05T04:00:00Z')),
            groups: [],
            value: 32,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-05T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-06T04:00:00Z')),
            groups: [],
            value: 10,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-06T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-07T04:00:00Z')),
            groups: [],
            value: 10,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-07T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-08T04:00:00Z')),
            groups: [],
            value: 13,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-08T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-09T04:00:00Z')),
            groups: [],
            value: 8,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-09T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-10T04:00:00Z')),
            groups: [],
            value: 2,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-10T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-11T04:00:00Z')),
            groups: [],
            value: 1,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-11T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-12T04:00:00Z')),
            groups: [],
            value: 39,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-12T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-13T04:00:00Z')),
            groups: [],
            value: 30,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-13T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-14T04:00:00Z')),
            groups: [],
            value: 40,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-14T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-15T04:00:00Z')),
            groups: [],
            value: 252,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-15T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-16T04:00:00Z')),
            groups: [],
            value: 209,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-16T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-17T04:00:00Z')),
            groups: [],
            value: 89,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-17T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-18T04:00:00Z')),
            groups: [],
            value: 91,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-18T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-19T04:00:00Z')),
            groups: [],
            value: 282,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-19T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-20T04:00:00Z')),
            groups: [],
            value: 237,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-20T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-21T04:00:00Z')),
            groups: [],
            value: 237,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-21T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-22T04:00:00Z')),
            groups: [],
            value: 288,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-22T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-23T04:00:00Z')),
            groups: [],
            value: 141,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-23T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-24T04:00:00Z')),
            groups: [],
            value: 45,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-24T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-25T04:00:00Z')),
            groups: [],
            value: 81,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-25T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-26T04:00:00Z')),
            groups: [],
            value: 173,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-26T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-27T04:00:00Z')),
            groups: [],
            value: 199,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-27T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-28T04:00:00Z')),
            groups: [],
            value: 172,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-28T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-29T04:00:00Z')),
            groups: [],
            value: 185,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-29T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-30T04:00:00Z')),
            groups: [],
            value: 140,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-30T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-07-31T04:00:00Z')),
            groups: [],
            value: 94,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-07-31T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-08-01T04:00:00Z')),
            groups: [],
            value: 76,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-08-01T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-08-02T04:00:00Z')),
            groups: [],
            value: 195,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-08-02T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-08-03T04:00:00Z')),
            groups: [],
            value: 220,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-08-03T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-08-04T04:00:00Z')),
            groups: [],
            value: 280,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-08-04T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-08-05T04:00:00Z')),
            groups: [],
            value: 273,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-08-05T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-08-06T04:00:00Z')),
            groups: [],
            value: 189,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-08-06T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-08-07T04:00:00Z')),
            groups: [],
            value: 182,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-08-07T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-08-08T04:00:00Z')),
            groups: [],
            value: 150,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-08-08T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-08-09T04:00:00Z')),
            groups: [],
            value: 213,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-08-09T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-08-10T04:00:00Z')),
            groups: [],
            value: 283,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-08-10T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-08-11T04:00:00Z')),
            groups: [],
            value: 364,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-08-11T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-08-12T04:00:00Z')),
            groups: [],
            value: 277,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-08-12T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-08-13T04:00:00Z')),
            groups: [],
            value: 215,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-08-13T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-08-14T04:00:00Z')),
            groups: [],
            value: 124,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-08-14T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-08-15T04:00:00Z')),
            groups: [],
            value: 110,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-08-15T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-08-16T04:00:00Z')),
            groups: [],
            value: 240,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-08-16T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-08-17T04:00:00Z')),
            groups: [],
            value: 530,
            defaultValue: 0,
            isPartialPeriod: false,
          },
          {
            startTime: Timestamp.fromDate(new Date('2022-08-17T04:00:00Z')),
            endTime: Timestamp.fromDate(new Date('2022-08-18T04:00:00Z')),
            groups: [],
            value: 257,
            defaultValue: 0,
            isPartialPeriod: false,
          },
        ],
        numGroups: int64.fromNumber(0),
      },
    },
    direction: 1,
    periodicStats: [
      {
        startDate: Timestamp.fromDate(new Date('2022-07-21T04:00:00Z')),
        endDate: Timestamp.fromDate(new Date('2022-07-28T04:00:00Z')),
        variability: {
          stdUpper: 230.6613874428116,
          stdLower: 83.33861255718841,
        },
      },
      {
        startDate: Timestamp.fromDate(new Date('2022-07-28T04:00:00Z')),
        endDate: Timestamp.fromDate(new Date('2022-08-04T04:00:00Z')),
        variability: {
          stdUpper: 236.50671287278342,
          stdLower: 103.49328712721658,
        },
      },
      {
        startDate: Timestamp.fromDate(new Date('2022-08-04T04:00:00Z')),
        endDate: Timestamp.fromDate(new Date('2022-08-11T04:00:00Z')),
        variability: {
          stdUpper: 304.95825878019866,
          stdLower: 167.6131697912299,
        },
      },
      {
        startDate: Timestamp.fromDate(new Date('2022-08-11T04:00:00Z')),
        endDate: Timestamp.fromDate(new Date('2022-08-18T04:00:00Z')),
        variability: {
          stdUpper: 379.1462660084511,
          stdLower: 121.71087684869175,
        },
      },
    ],
    trendSteps: null,
    ratioChange: 1.7474242482849252,
  },
};

export const MOCK_TREND_GROUP_BY: Insight = {
  insightId: 'pDSxuQyn03F4',
  kind: 1,
  computationKind: 6,
  computationHash: 'testComputationHash',
  dxObject: {
    id: 'test',
    version: int64.fromNumber(0),
    supportedDxObjectType: 1,
  },
  alertMetadata: null,
  computedAt: Timestamp.fromDate(new Date('2022-09-12T17:36:31.414370656Z')),
  start: Timestamp.fromDate(new Date('2022-08-22T04:00:00Z')),
  end: Timestamp.fromDate(new Date('2022-09-12T04:00:00Z')),
  createdAt: Timestamp.fromDate(new Date('2022-09-12T17:36:31.438559Z')),
  updatedAt: Timestamp.fromDate(new Date('2022-09-12T17:36:31.438559Z')),
  trend: null,
  trendGroup: {
    periodicTables: [
      {
        aggregateTable: {
          metricsDataTable: {
            table: [
              {
                startTime: Timestamp.fromDate(new Date('2022-08-22T04:00:00Z')),
                endTime: Timestamp.fromDate(new Date('2022-08-29T04:00:00Z')),
                groups: [
                  {
                    groupId: 'product-mgmt',
                    fallback: false,
                  },
                ],
                value: 1.2857142857142858,
                defaultValue: 0,
                isPartialPeriod: false,
              },
              {
                startTime: Timestamp.fromDate(new Date('2022-08-22T04:00:00Z')),
                endTime: Timestamp.fromDate(new Date('2022-08-29T04:00:00Z')),
                groups: [
                  {
                    groupId: 'acct-mgmt',
                    fallback: false,
                  },
                ],
                value: 0.7142857142857143,
                defaultValue: 0,
                isPartialPeriod: false,
              },
              {
                startTime: Timestamp.fromDate(new Date('2022-08-22T04:00:00Z')),
                endTime: Timestamp.fromDate(new Date('2022-08-29T04:00:00Z')),
                groups: [
                  {
                    groupId: 'data',
                    fallback: false,
                  },
                ],
                value: 0.42857142857142855,
                defaultValue: 0,
                isPartialPeriod: false,
              },
              {
                startTime: Timestamp.fromDate(new Date('2022-08-22T04:00:00Z')),
                endTime: Timestamp.fromDate(new Date('2022-08-29T04:00:00Z')),
                groups: [
                  {
                    groupId: '',
                    fallback: false,
                  },
                ],
                value: 0,
                defaultValue: 0,
                isPartialPeriod: false,
              },
              {
                startTime: Timestamp.fromDate(new Date('2022-08-22T04:00:00Z')),
                endTime: Timestamp.fromDate(new Date('2022-08-29T04:00:00Z')),
                groups: [
                  {
                    groupId: 'engineering',
                    fallback: false,
                  },
                ],
                value: 0,
                defaultValue: 0,
                isPartialPeriod: false,
              },
            ],
            numGroups: int64.fromNumber(0),
          },
        },
        kneeIndex: 1,
        top: {
          groups: [
            {
              groupId: 'product-mgmt',
              fallback: false,
            },
          ],
          value: 1.2857142857142858,
          triggerType: TriggerType.TRIGGER_TYPE_CROSSED,
        },
        gap: 0.5714285714285715,
      },
      {
        aggregateTable: {
          metricsDataTable: {
            table: [
              {
                startTime: Timestamp.fromDate(new Date('2022-08-29T04:00:00Z')),
                endTime: Timestamp.fromDate(new Date('2022-09-05T04:00:00Z')),
                groups: [
                  {
                    groupId: 'product-mgmt',
                    fallback: false,
                  },
                ],
                value: 1.1428571428571428,
                defaultValue: 0,
                isPartialPeriod: false,
              },
              {
                startTime: Timestamp.fromDate(new Date('2022-08-29T04:00:00Z')),
                endTime: Timestamp.fromDate(new Date('2022-09-05T04:00:00Z')),
                groups: [
                  {
                    groupId: 'engineering',
                    fallback: false,
                  },
                ],
                value: 0.8571428571428571,
                defaultValue: 0,
                isPartialPeriod: false,
              },
              {
                startTime: Timestamp.fromDate(new Date('2022-08-29T04:00:00Z')),
                endTime: Timestamp.fromDate(new Date('2022-09-05T04:00:00Z')),
                groups: [
                  {
                    groupId: 'acct-mgmt',
                    fallback: false,
                  },
                ],
                value: 0.7142857142857143,
                defaultValue: 0,
                isPartialPeriod: false,
              },
              {
                startTime: Timestamp.fromDate(new Date('2022-08-29T04:00:00Z')),
                endTime: Timestamp.fromDate(new Date('2022-09-05T04:00:00Z')),
                groups: [
                  {
                    groupId: '',
                    fallback: false,
                  },
                ],
                value: 0.14285714285714285,
                defaultValue: 0,
                isPartialPeriod: false,
              },
              {
                startTime: Timestamp.fromDate(new Date('2022-08-29T04:00:00Z')),
                endTime: Timestamp.fromDate(new Date('2022-09-05T04:00:00Z')),
                groups: [
                  {
                    groupId: 'data',
                    fallback: false,
                  },
                ],
                value: 0.14285714285714285,
                defaultValue: 0,
                isPartialPeriod: false,
              },
            ],
            numGroups: int64.fromNumber(0),
          },
        },
        kneeIndex: 1,
        top: {
          groups: [
            {
              groupId: 'product-mgmt',
              fallback: false,
            },
          ],
          value: 1.1428571428571428,
          triggerType: TriggerType.TRIGGER_TYPE_CROSSED,
        },
        gap: 0.2857142857142857,
      },
      {
        aggregateTable: {
          metricsDataTable: {
            table: [
              {
                startTime: Timestamp.fromDate(new Date('2022-09-05T04:00:00Z')),
                endTime: Timestamp.fromDate(new Date('2022-09-12T04:00:00Z')),
                groups: [
                  {
                    groupId: 'product-mgmt',
                    fallback: false,
                  },
                ],
                value: 1.8571428571428572,
                defaultValue: 0,
                isPartialPeriod: false,
              },
              {
                startTime: Timestamp.fromDate(new Date('2022-09-05T04:00:00Z')),
                endTime: Timestamp.fromDate(new Date('2022-09-12T04:00:00Z')),
                groups: [
                  {
                    groupId: 'engineering',
                    fallback: false,
                  },
                ],
                value: 0.8571428571428571,
                defaultValue: 0,
                isPartialPeriod: false,
              },
              {
                startTime: Timestamp.fromDate(new Date('2022-09-05T04:00:00Z')),
                endTime: Timestamp.fromDate(new Date('2022-09-12T04:00:00Z')),
                groups: [
                  {
                    groupId: '',
                    fallback: false,
                  },
                ],
                value: 0.2857142857142857,
                defaultValue: 0,
                isPartialPeriod: false,
              },
              {
                startTime: Timestamp.fromDate(new Date('2022-09-05T04:00:00Z')),
                endTime: Timestamp.fromDate(new Date('2022-09-12T04:00:00Z')),
                groups: [
                  {
                    groupId: 'acct-mgmt',
                    fallback: false,
                  },
                ],
                value: 0.2857142857142857,
                defaultValue: 0,
                isPartialPeriod: false,
              },
              {
                startTime: Timestamp.fromDate(new Date('2022-09-05T04:00:00Z')),
                endTime: Timestamp.fromDate(new Date('2022-09-12T04:00:00Z')),
                groups: [
                  {
                    groupId: 'data',
                    fallback: false,
                  },
                ],
                value: 0,
                defaultValue: 0,
                isPartialPeriod: false,
              },
            ],
            numGroups: int64.fromNumber(0),
          },
        },
        kneeIndex: 1,
        top: {
          groups: [
            {
              groupId: 'product-mgmt',
              fallback: false,
            },
          ],
          value: 1.8571428571428572,
          triggerType: TriggerType.TRIGGER_TYPE_CROSSED,
        },
        gap: 1,
      },
    ],
    topValueChange: {
      direction: 1,
      ratioChange: 1.5294117647058822,
      previousMean: 1.2142857142857144,
    },
    gapChange: {
      direction: 1,
      ratioChange: 2.333333333333333,
      previousMean: 0.4285714285714286,
    },
    featuresDetected: 3,
  },
};
